import { collectsPayment, Track } from '@bariendo/lib/constants';

/**
 * What a track switch does, and which destinations an operator may pick.
 *
 * Lives in its own module rather than beside the component so the component
 * file exports only components (`react-refresh/only-export-components`), and
 * pure so the whole table can be asserted without rendering anything.
 *
 * **This mirrors `bariendo-be`'s `PARTNER_CONFIG`; it does not enforce it.** The
 * backend is the authority — it re-derives every rule below and refuses the
 * call if we got one wrong, which is the safe direction. This exists so the
 * operator reads what is about to happen instead of meeting a 400.
 */

/**
 * Whether a track's patients are pushed to the non-HIPAA Close CRM. Mirrors
 * `PARTNER_CONFIG[...].sendToClose`.
 *
 * Only `d2c` does, which is the whole content of the acknowledgement rule:
 * leaving `d2c` scrubs a Close record, and nothing else does. A total record so
 * a new `Track` will not compile until someone decides its answer.
 */
const SYNCS_TO_CLOSE: Record<Track, boolean> = {
  [Track.D2C]: true,
  [Track.CARRUM]: false,
  [Track.TRANSCARENT]: false,
  [Track.EMPLOYER]: false,
  [Track.INSURANCE]: false,
};

/**
 * Self-pay first, then insurance, then the employer-benefit destinations from
 * least to most specific.
 */
const DESTINATION_ORDER: readonly Track[] = [
  Track.D2C,
  Track.INSURANCE,
  Track.EMPLOYER,
  Track.CARRUM,
  Track.TRANSCARENT,
];

/**
 * What the operator reads in the list. `TrackLabel` is the pill's vocabulary and
 * too terse here — "Carrum" alone does not say it is an employer benefit.
 */
const DESTINATION_LABEL: Record<Track, string> = {
  [Track.D2C]: 'Self-pay',
  [Track.INSURANCE]: 'Insurance',
  [Track.EMPLOYER]: 'Employer benefit · unverified',
  [Track.CARRUM]: 'Employer benefit · Carrum',
  [Track.TRANSCARENT]: 'Employer benefit · Transcarent',
};

/**
 * What the operator ticks before a move that scrubs a Close record. One string
 * for the Switch track dialog and the PN card's coverage buttons (BAR-2921).
 */
export const TRACK_SWITCH_ACKNOWLEDGEMENT = 'I understand this scrubs their Close record and cannot be undone';

export interface TrackDestination {
  track: Track;
  label: string;
}

// Every destination is reachable now: BE-9 gave Transcarent a program duration
// and a payor, and the administrator became its own field rather than something
// only the eligibility flow could set.
export const TRACK_DESTINATIONS: readonly TrackDestination[] = DESTINATION_ORDER.map((track) => ({
  track,
  label: DESTINATION_LABEL[track],
}));

export interface TrackTransition {
  /** What happens to the patient's record in the Close CRM. */
  closeRecord: 'scrubbed' | 'recreated' | 'unchanged';
  /** Whether the backend will refuse without `acknowledgeRetroactivePhiExposure`. */
  needsAcknowledgement: boolean;
  /** Consequences, in the order the dialog lists them. */
  effects: string[];
}

/**
 * The consequences of moving a patient from one track to another.
 *
 * An **unresolved** `from` is treated as `d2c`, matching `getPartnerConfig`'s
 * `?? D2C` fallback. This is the common case, not an edge one: the eligibility
 * flow opens no `EpisodeOfCare` for a self-pay lead, and the episode is the only
 * thing Orbit can read — so most self-pay patients resolve to `undefined`.
 * Assuming `d2c` is the safe direction. It produces the warning and asks for the
 * acknowledgement on a switch that may well scrub a Close record, and the
 * backend ignores the flag on a transition that does not need it. The dialog
 * says the assumption out loud rather than presenting it as a known fact.
 */
export function trackTransition(from: Track | undefined, to: Track): TrackTransition {
  const resolvedFrom = from ?? Track.D2C;

  const turnsCloseOff = SYNCS_TO_CLOSE[resolvedFrom] && !SYNCS_TO_CLOSE[to];
  const turnsCloseOn = !SYNCS_TO_CLOSE[resolvedFrom] && SYNCS_TO_CLOSE[to];

  const closeRecord = turnsCloseOff ? 'scrubbed' : turnsCloseOn ? 'recreated' : 'unchanged';

  const effects: string[] = [];

  if (turnsCloseOff) {
    effects.push('Their record in the Close CRM is scrubbed of personal details, and CRM sync freezes.');
  } else if (turnsCloseOn) {
    effects.push('Their Close CRM record is recreated and the intro workflows restart.');
  } else {
    effects.push('Their Close CRM record is untouched — neither track syncs to Close.');
  }

  // Always true: the switch opens the new episode and then closes the old one,
  // whichever direction it runs in.
  effects.push('Their current care episode is closed and a new one opened.');

  if (collectsPayment(resolvedFrom) && !collectsPayment(to)) {
    effects.push('They will no longer be asked to pay, and cannot be charged.');
  } else if (!collectsPayment(resolvedFrom) && collectsPayment(to)) {
    effects.push('They will be asked to pay for treatment.');
  }

  // Not automated anywhere, and the one consequence an operator has to act on
  // themselves — so it is stated on every switch, not just the destructive one.
  effects.push('Any open quote stays open — cancel it separately.');

  return { closeRecord, needsAcknowledgement: turnsCloseOff, effects };
}
